import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import { message } from '../services/toastMessage';
import { motion } from 'framer-motion';
import { IconMailOpened } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { resendConfirmation } from '../services/AuthenticationService';
import { translate } from '../helpers/translationHelper';
import { TRANSLATIONS } from '../constants/translations';
import { TEAL, TEAL_LIGHT } from '../theme/creamyTokens';

interface ConfirmationPendingNoticeProps {
  email: string;
  title: string;
  description: string;
}

/**
 * Shared "check your email" card -- used right after registering and on
 * Login's EMAIL_NOT_CONFIRMED case, so both moments look and behave the
 * same. Deliberately its own gentle floating-envelope animation rather
 * than reusing Confirm.tsx's confetti burst: that one is for the "you're
 * in" moment, this one is for "not yet".
 */
// Client-side spam guard, independent of (and shorter than) the backend's
// own real limit (5 requests / 15 min per IP, see AuthController's
// [EnableRateLimiting("AccountAction")] on resend-confirmation) -- this is
// just what stops someone from mashing the button, not the actual defense.
const RESEND_COOLDOWN_SECONDS = 60;

const ConfirmationPendingNotice: React.FC<ConfirmationPendingNoticeProps> = ({ email, title, description }) => {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setCooldown((seconds) => (seconds > 0 ? seconds - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleResend = async () => {
    setSending(true);
    try {
      await resendConfirmation(email);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      message.success(translate(t, TRANSLATIONS.RESEND_CONFIRMATION_SUCCESS));
    } catch (error: any) {
      // The backend's own limit was hit -- no ApiResponse body to read a
      // message from (see ApiService.parseErrorMessage), so start the same
      // local cooldown rather than letting the button invite another
      // immediate (and immediately rejected) click.
      if (error.message === 'RATE_LIMITED') {
        setCooldown(RESEND_COOLDOWN_SECONDS);
        message.error(translate(t, TRANSLATIONS.RESEND_CONFIRMATION_RATE_LIMITED));
      } else {
        message.error(error.message || translate(t, TRANSLATIONS.RESEND_CONFIRMATION_ERROR_FALLBACK));
      }
    } finally {
      setSending(false);
    }
  };

  const onCooldown = cooldown > 0;

  return (
    <div style={{ maxWidth: 440, width: '100%', margin: '0 auto', padding: '0 16px' }}>
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: '#FFFFFF',
          borderRadius: 28,
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          padding: '48px 36px 40px',
          textAlign: 'center',
        }}
      >
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 220, damping: 16 }}
          style={{
            width: 84,
            height: 84,
            margin: '0 auto',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${TEAL}, ${TEAL_LIGHT})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(29,158,117,0.35)',
          }}
        >
          <motion.div
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <IconMailOpened size={40} color="#FFFFFF" stroke={1.8} />
          </motion.div>
        </motion.div>

        <h2 style={{ marginTop: 24, marginBottom: 0, fontSize: 24, fontWeight: 700 }}>{title}</h2>
        <p style={{ marginTop: 8, color: '#667085', fontSize: 15, lineHeight: 1.6 }}>{description}</p>
        <p style={{ marginTop: 4, color: '#344054', fontSize: 14, fontWeight: 600, wordBreak: 'break-all' }}>{email}</p>

        <Button
          shape="round"
          size="large"
          loading={sending}
          disabled={onCooldown}
          style={{ marginTop: 24, minWidth: 220 }}
          onClick={handleResend}
        >
          {onCooldown
            ? translate(t, TRANSLATIONS.RESEND_CONFIRMATION_COOLDOWN, { seconds: cooldown })
            : translate(t, TRANSLATIONS.RESEND_CONFIRMATION_CTA)}
        </Button>
      </div>
    </div>
  );
};

export default ConfirmationPendingNotice;
